import { useCallback } from 'react';
import BarChartScenario from '../bar-chart-scenario/BarChartScenario';
import { HistogramChartByProps } from '../config';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { DateTime } from 'luxon';
import { ScenarioDataSetLabelV2, ScenarioQueryParams } from '@customer/store/apis/scenariosApi';
import { GetTooltipTitle } from '../utils';
import { CustomBarChartLabel, OnCategorySelect } from '../bar-select/BarSelectPlugin';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { Hours } from '@customer/store/types';
import { isEnumValue } from '@cas/utils/typeHelpers';
import {
  saveFilters,
  setTimeOfDayEnd,
  setTimeOfDayStart,
} from '@customer/store/slices/filtersSlice';
import { FilterGroup } from '@customer/filters/types';
// import { VILevel, VIType } from "@customer/store/apis/interactionsApi";

// TODO: Re-enable the commented code after the UX for bar segment selection has been improved.

export const HistogramChartScenario = <FiltersSubset extends ScenarioQueryParams>(
  props: HistogramChartByProps,
) => {
  const dispatch = useAppDispatch();
  const siteId = useAppSelector(state => state.auth.siteId);

  const { showHourOnly, showTimeOnly } = useLocaleFormatter();

  const getHourOfDay = useCallback(
    (label: ScenarioDataSetLabelV2) => {
      const num = label.id;
      const timeOfDay = DateTime.now().startOf('day').plus({ hours: num });
      return showHourOnly(timeOfDay);
    },
    [showHourOnly],
  );

  const getTooltipTitle = useCallback<GetTooltipTitle>(
    index => {
      const num = Number(index);
      const start = DateTime.now().startOf('day').plus({ hours: num });
      const end = start.plus({ hours: 1 });

      return `${showTimeOnly(start, true)} - ${showTimeOnly(end, true)}`;
    },
    [showTimeOnly],
  );

  // Allow filtering by time of day.
  const onGICategorySelect = useCallback<OnCategorySelect>(
    ({ name: timeOfDayStart }: CustomBarChartLabel): void => {
      if (siteId) {
        // Get the next hour of the day as a string.
        const timeOfDayEnd = ((Number(timeOfDayStart) + 1) % 24).toString();

        // Confirm that the selected assetGroup is within the vehicleGroups list.
        if (isEnumValue(timeOfDayStart, Hours) && isEnumValue(timeOfDayEnd, Hours)) {
          dispatch(setTimeOfDayStart(timeOfDayStart));
          dispatch(setTimeOfDayEnd(timeOfDayEnd));
        }

        dispatch(saveFilters(siteId));
      }
    },
    [dispatch, siteId],
  );

  // Allow filtering by VI Type.
  // const onVIBarSelect = useCallback<OnBarSelect>((_: CustomBarChartLabel, datasetId: string): void => {
  //   if (siteId && isEnumValue(datasetId, VILevel)) {
  //     const typesMatchingLevel: VIType[] = Object.values(VIType).filter((type: VIType) => type.endsWith(datasetId));
  //     dispatch(setVehicleInteractionTypes(typesMatchingLevel));
  //     dispatch(saveFilters(siteId));
  //   }
  // }, [dispatch, siteId]);

  const isGIFilterGroup =
    props.filterGroup === FilterGroup.AssetGI || props.filterGroup === FilterGroup.PerformanceGI;

  return (
    <BarChartScenario<FiltersSubset>
      {...props}
      histogram={true}
      getCategoryLabel={getHourOfDay}
      getTooltipTitle={getTooltipTitle}
      // Only add an onCategorySelect callback for GI filter groups.
      onCategorySelect={isGIFilterGroup ? onGICategorySelect : undefined}
      // Switch the callback depending on the filter group.
      // Temporarily use the same function for onBarSelect until the UX is improved.
      // onBarSelect={isGIFilterGroup ? onGICategorySelect : onVIBarSelect}
      onBarSelect={isGIFilterGroup ? onGICategorySelect : undefined}
    />
  );
};

export default HistogramChartScenario;
